import React, { useState, useEffect, useRef } from 'react';

// ── Design tokens ─────────────────────────────────────────────────────────────
const N = '#0f172a';       // navy primary
const B = '#3b82f6';       // blue accent
const BH = '#2563eb';      // blue hover
const W = '#ffffff';
const G = '#f8fafc';       // light gray bg
const BD = '#e2e8f0';      // border
const T1 = '#0f172a';      // text primary
const T2 = '#475569';      // text secondary
const T3 = '#94a3b8';      // text muted
const GR = '#10b981';      // green
const AM = '#f59e0b';      // amber
const RD = '#ef4444';      // red
const FH = "'Plus Jakarta Sans', 'DM Sans', sans-serif";
const FB = "'Inter', 'DM Sans', sans-serif";

// ── Intersection Observer hook ─────────────────────────────────────────────────
function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function FadeIn({ children, delay = 0, style = {} }) {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? 'translateY(0)' : 'translateY(28px)',
      transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
      ...style,
    }}>
      {children}
    </div>
  );
}

// ── Logo ──────────────────────────────────────────────────────────────────────
function Logo({ light = false }) {
  const text = light ? W : N;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
      <svg width="30" height="30" viewBox="0 0 40 40" fill="none">
        <rect width="40" height="40" rx="10" fill={light ? 'rgba(255,255,255,0.15)' : B}/>
        <path d="M20 6L8 11v10c0 7.18 5.13 13.89 12 15.5C27.87 34.89 33 28.18 33 21V11L20 6z"
          fill="rgba(255,255,255,0.2)" stroke="rgba(255,255,255,0.5)" strokeWidth="1"/>
        <path d="M14 20.5l4 4 8-8" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <span style={{ fontSize: 17, fontWeight: 700, color: text, letterSpacing: '-0.01em', fontFamily: FH }}>
        TheClearAudit
      </span>
    </div>
  );
}

// ── Hero Dashboard Mockup ──────────────────────────────────────────────────────
function DashboardMockup() {
  const domains = [
    { name: 'Access Control',        pct: 95, c: GR },
    { name: 'Identification & Auth', pct: 88, c: GR },
    { name: 'Config Management',     pct: 80, c: GR },
    { name: 'Incident Response',     pct: 92, c: GR },
    { name: 'Risk Assessment',       pct: 75, c: AM },
    { name: 'System Protection',     pct: 82, c: GR },
  ];
  return (
    <div style={{
      background: W, borderRadius: 16,
      boxShadow: '0 32px 80px rgba(15,23,42,0.18), 0 4px 16px rgba(15,23,42,0.08)',
      border: `1px solid ${BD}`, overflow: 'hidden',
      width: '100%', maxWidth: 480,
    }}>
      {/* Window chrome */}
      <div style={{
        background: '#f1f5f9', borderBottom: `1px solid ${BD}`,
        padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 7,
      }}>
        {[RD, AM, GR].map((c, i) => (
          <div key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: c }}/>
        ))}
        <div style={{ flex: 1, textAlign: 'center', fontSize: 11, color: T3, fontWeight: 500 }}>
          TheClearAudit — CMMC Level 2 Dashboard
        </div>
      </div>
      <div style={{ padding: '18px 20px' }}>
        {/* Header row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: T1, fontFamily: FH }}>CMMC Level 2 Readiness</div>
            <div style={{ fontSize: 10, color: T3 }}>110 controls · Assessment complete</div>
          </div>
          <div style={{
            background: '#dcfce7', border: `1px solid ${GR}40`, borderRadius: 20,
            padding: '4px 12px', fontSize: 11, fontWeight: 600, color: '#166534',
          }}>87% Complete</div>
        </div>
        {/* Stat cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
          {[
            { label: 'Readiness', value: '87%', color: GR },
            { label: 'Controls Met', value: '96', color: GR },
            { label: 'Open Gaps', value: '14', color: AM },
          ].map(s => (
            <div key={s.label} style={{
              background: G, borderRadius: 8, padding: '10px 12px', border: `1px solid ${BD}`,
            }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: s.color, lineHeight: 1, marginBottom: 2, fontFamily: FH }}>{s.value}</div>
              <div style={{ fontSize: 10, color: T3 }}>{s.label}</div>
            </div>
          ))}
        </div>
        {/* Domain bars */}
        <div style={{ fontSize: 11, fontWeight: 600, color: T2, marginBottom: 10, fontFamily: FH }}>Domain Readiness</div>
        {domains.map(d => (
          <div key={d.name} style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ fontSize: 10, color: T2 }}>{d.name}</span>
              <span style={{ fontSize: 10, fontWeight: 600, color: d.c }}>{d.pct}%</span>
            </div>
            <div style={{ height: 4, background: BD, borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${d.pct}%`, background: d.c, borderRadius: 2 }}/>
            </div>
          </div>
        ))}
        {/* Alert footer */}
        <div style={{
          marginTop: 14, padding: '9px 12px',
          background: '#eff6ff', borderRadius: 8,
          display: 'flex', alignItems: 'center', gap: 7,
        }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: B, flexShrink: 0 }}/>
          <span style={{ fontSize: 11, color: B }}>Assessment complete — 14 gaps identified. Download your SSP and POA&M to share with your C3PAO.</span>
        </div>
      </div>
    </div>
  );
}

// ── Icon helpers ──────────────────────────────────────────────────────────────
const ICON_PATHS = {
  shield:    <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
  list:      <><path d="M9 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/></>,
  search:    <><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>,
  folder:    <><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></>,
  download:  <><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></>,
  message:   <><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></>,
  layers:    <><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></>,
  check:     <><polyline points="20 6 9 17 4 12"/></>,
  users:     <><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></>,
  lock:      <><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></>,
};

function Ico({ k, size = 20, color = B }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {ICON_PATHS[k]}
    </svg>
  );
}

// ── Checkmark ─────────────────────────────────────────────────────────────────
function Check({ color = GR }) {
  return (
    <div style={{
      width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
      background: `${color}18`, border: `1.5px solid ${color}50`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
        <path d="M2 5.5l2 2 4-4" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function Landing({ onSignUp, onLogin, onTerms, onPrivacy, onSecurity, onDPA, onAUP, onSLA, onCUI }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openFaq, setOpenFaq]   = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const el = document.getElementById('landing-root');
    if (!el) return;
    const fn = () => setScrolled(el.scrollTop > 30);
    el.addEventListener('scroll', fn);
    return () => el.removeEventListener('scroll', fn);
  }, []);

  function scrollTo(id) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  }

  const sec = (bg, border = 'transparent') => ({
    background: bg,
    borderTop: border !== 'transparent' ? `1px solid ${border}` : 'none',
    borderBottom: border !== 'transparent' ? `1px solid ${border}` : 'none',
    padding: '88px 48px',
  });

  const features = [
    { icon: 'shield', title: 'CMMC Control Dashboard', desc: 'Real-time compliance score across all 110 controls with gap analysis, domain readiness, and prioritized remediation tracking.' },
    { icon: 'message', title: 'AI-Powered Guidance', desc: 'Ask compliance questions and get instant answers based on NIST 800-171 and CMMC 2.0 — without sharing sensitive or classified data.' },
    { icon: 'list', title: 'Policy Template Library', desc: '7 pre-built, audit-ready policy templates covering all CMMC domains. Download, customize, and attach directly to your SSP.' },
    { icon: 'folder', title: 'Evidence Locker', desc: 'Securely store and organize all compliance evidence mapped directly to the controls they satisfy. One-click retrieval at audit time.' },
    { icon: 'download', title: 'One-Click PDF Exports', desc: 'Generate professional SSP and POA&M documents formatted for C3PAO submission. No formatting, no manual assembly.' },
    { icon: 'layers', title: 'Multi-Framework', desc: 'One platform for CMMC, SOC 2, ISO 27001, HIPAA, FedRAMP, PCI DSS, and NIST CSF 2.0. Switch frameworks without losing data.' },
  ];

  const plans = [
    {
      name: 'Professional', price: '$399', period: '/mo', setup: '$499 setup fee',
      popular: false,
      desc: 'Start your CMMC compliance journey before the November 2026 deadline.',
      features: ['CMMC L1, L2, L3 + NIST 800-171', 'Up to 3 team members', '3 frameworks included', 'Gap analysis & readiness score', 'SSP & POA&M PDF export', 'Policy template library', 'AI compliance assistant'],
    },
    {
      name: 'Business', price: '$799', period: '/mo', setup: '$799 setup fee',
      popular: true,
      desc: 'The complete compliance platform for the November 2026 CMMC deadline.',
      features: ['Everything in Professional', 'All 8 compliance frameworks', 'Unlimited team seats', 'Priority support', 'Dedicated onboarding call', 'SSP spreadsheet export', 'Auditor access portal', 'Annual affirmation tracker'],
    },
  ];

  const faqs = [
    {
      q: 'Do I need CMMC Level 1 or Level 2?',
      a: 'CMMC Level 1 applies to contractors that handle only Federal Contract Information (FCI) — the basic information created or provided under a government contract. It covers 17 practices. CMMC Level 2 applies to contractors that handle Controlled Unclassified Information (CUI) and covers all 110 NIST SP 800-171 controls. Your contract language will specify which level is required. Most prime contractors and their subcontractors that touch CUI require Level 2. If your contract references DFARS clause 252.204-7012, you almost certainly need Level 2.',
    },
    {
      q: 'How long does CMMC Level 2 preparation take?',
      a: 'Most small-to-medium defense contractors need 6–18 months to fully prepare for a Level 2 C3PAO assessment. The timeline depends heavily on your starting security posture. TheClearAudit helps you identify exactly where you stand on day one with a full gap analysis across all 110 controls. Contractors with existing IT policies and security practices typically need 6–9 months. Organizations starting from scratch typically need 12–18 months. With the November 2026 Phase 2 deadline, now is the time to start.',
    },
    {
      q: 'Is my compliance data secure on TheClearAudit?',
      a: 'Yes. All data is encrypted at rest using AES-256 and in transit using TLS 1.3. We use row-level security at the database layer so your data is completely isolated from other organizations — even in the event of an application vulnerability. Our infrastructure is hosted on AWS US-East-1 via Supabase and Vercel, and your data never leaves US borders. We strongly advise against entering any classified, CUI, or sensitive contract information into the platform.',
    },
    {
      q: 'Can I use TheClearAudit for frameworks other than CMMC?',
      a: 'Yes. TheClearAudit supports 8 compliance frameworks: CMMC 2.0 (Levels 1, 2, and 3), NIST SP 800-171, SOC 2 Type II, ISO 27001:2022, HIPAA Security Rule, NIST CSF 2.0, PCI DSS v4.0, and FedRAMP Moderate. You can switch between frameworks from your dashboard at any time without losing your assessment data. The Professional plan includes CMMC and NIST; Business adds all remaining frameworks.',
    },
    {
      q: 'What is included in the policy template library?',
      a: 'The Professional plan includes 7 pre-built, audit-ready policy templates that map directly to CMMC domains: Access Control Policy, Incident Response Policy, Configuration Management Policy, Media Protection Policy, System and Communications Protection Policy, Security Awareness and Training Policy, and Risk Assessment Policy. Each template is a professional Word document pre-filled with your company name and CAGE code, ready for review and signature. You can download, customize, and attach to your evidence locker in minutes.',
    },
    {
      q: 'How does the auditor access work?',
      a: 'The auditor access feature (Professional plan and above) lets you generate a read-only access link for your C3PAO assessor or internal auditor. The link grants time-limited, view-only access to your assessment data, gap analysis, and evidence inventory — without requiring the auditor to create an account. You control who has access and can revoke it at any time. This is designed to make the pre-assessment evidence review process as smooth as possible.',
    },
    {
      q: 'Can I export my SSP and POA&M for my C3PAO?',
      a: 'Yes. With a Professional plan or higher, TheClearAudit generates a complete System Security Plan (SSP) and Plan of Action and Milestones (POA&M) as formatted PDFs with one click. The SSP includes your system information, control assessment results organized by domain, and your evidence inventory. The POA&M includes all open remediation items organized by priority and due date. These documents are formatted for C3PAO review. Note: these are preparation documents — your official SSP submitted to the DoD must be based on a formal self-assessment.',
    },
    {
      q: 'What happens after I achieve CMMC certification?',
      a: 'CMMC Level 2 certification is valid for 3 years, but compliance is not a one-time event. You must maintain your security controls continuously and submit an annual SPRS score affirmation to the DoD SPRS portal. TheClearAudit\'s annual affirmation tracker (Pro and above) helps you document your annual self-assessment and maintain your compliance posture year-round. When your 3-year certification expires, you\'ll need to go through the C3PAO assessment process again — TheClearAudit will have all your historical data ready to make that process significantly easier.',
    },
    {
      q: 'What is the November 2026 CMMC deadline and what happens if I miss it?',
      a: 'CMMC Phase 2 enforcement begins in November 2026. After that date, all DoD contracts will require CMMC Level 2 certification for contractors handling Controlled Unclassified Information (CUI). If you\'re not certified, you cannot bid on or fulfill DoD contracts. TheClearAudit is designed to get you audit-ready before that deadline.',
    },
    {
      q: 'How is TheClearAudit different from hiring a CMMC consultant?',
      a: 'CMMC consultants typically charge $25,000–$75,000 for full certification support. TheClearAudit gives you the same gap analysis, documentation, policy templates, and audit-ready exports at a fraction of the cost. You stay in control of your compliance program, with AI-powered guidance available 24/7. Many contractors use TheClearAudit alongside a consultant to reduce billable hours.',
    },
  ];

  const trustCards = [
    { icon: 'lock',  title: 'AES-256 Encryption', desc: 'All data encrypted at rest and in transit using TLS 1.3. Encryption keys managed by Supabase with automatic rotation.' },
    { icon: 'shield', title: 'US-Based Infrastructure', desc: 'Hosted on AWS US-East-1 via Supabase and Vercel. Your data never leaves United States borders.' },
    { icon: 'users',  title: 'Role-Based Access', desc: 'Granular permissions with MFA support, row-level security, session expiration, and complete audit logs.' },
    { icon: 'check',  title: 'SOC 2 Planned', desc: 'SOC 2 Type I audit planned for Q4 2026. Security documentation and architecture details available on request.' },
  ];

  const navLinks = [
    { label: 'Product',    action: () => scrollTo('features') },
    { label: 'Frameworks', action: () => scrollTo('frameworks') },
    { label: 'Pricing',    action: () => scrollTo('pricing') },
    { label: 'Security',   action: onSecurity || (() => scrollTo('trust')) },
  ];

  return (
    <div id="landing-root" style={{ fontFamily: FB, background: W, color: T1, height: '100vh', overflowY: 'auto', overflowX: 'hidden' }}>
      <style>{`
        @keyframes heroFloat {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-8px); }
        }
        @keyframes ldpulse {
          0%, 100% { opacity: 0.4; transform: scale(0.85); }
          50%       { opacity: 1;   transform: scale(1);    }
        }
        .ld-nav-link { transition: color 0.15s; }
        .ld-nav-link:hover { color: ${W} !important; }
        .ld-btn-primary {
          background: ${B}; color: ${W}; border: 2px solid ${B};
          border-radius: 8px; font-weight: 600; cursor: pointer;
          font-family: ${FB}; transition: background 0.15s, box-shadow 0.15s;
        }
        .ld-btn-primary:hover { background: ${BH}; border-color: ${BH}; box-shadow: 0 4px 16px rgba(59,130,246,0.3); }
        .ld-btn-outline {
          background: transparent; color: ${T2}; border: 2px solid ${BD};
          border-radius: 8px; font-weight: 500; cursor: pointer;
          font-family: ${FB}; transition: all 0.15s;
        }
        .ld-btn-outline:hover { border-color: ${B}; color: ${B}; }
        .ld-btn-white {
          background: ${W}; color: ${B}; border: 2px solid ${W};
          border-radius: 8px; font-weight: 600; cursor: pointer;
          font-family: ${FB}; transition: all 0.15s;
        }
        .ld-btn-white:hover { background: #eff6ff; }
        .ld-btn-outline-white {
          background: transparent; color: rgba(255,255,255,0.85); border: 2px solid rgba(255,255,255,0.35);
          border-radius: 8px; font-weight: 500; cursor: pointer;
          font-family: ${FB}; transition: all 0.15s;
        }
        .ld-btn-outline-white:hover { border-color: rgba(255,255,255,0.7); color: ${W}; }
        .ld-feature-card { transition: transform 0.2s, box-shadow 0.2s; }
        .ld-feature-card:hover { transform: translateY(-3px); box-shadow: 0 12px 40px rgba(15,23,42,0.1); }
        .ld-plan-card { transition: transform 0.2s, box-shadow 0.2s; }
        .ld-plan-card:hover { transform: translateY(-4px); box-shadow: 0 16px 48px rgba(15,23,42,0.12); }
        .ld-faq-q { transition: color 0.15s; }
        .ld-faq-q:hover { color: ${B} !important; }
        @media (max-width: 768px) {
          .ld-nav-links { display: none !important; }
          .ld-hamburger { display: flex !important; }
          .ld-nav { padding: 0 16px !important; }
          .ld-hero-section { padding: 56px 20px 40px !important; }
          .ld-frameworks-bar { padding: 20px 16px !important; }
          .ld-hero-grid { grid-template-columns: 1fr !important; }
          .ld-features-grid { grid-template-columns: 1fr !important; }
          .ld-testimonials-grid { grid-template-columns: 1fr !important; }
          .ld-pricing-grid { grid-template-columns: 1fr !important; }
          .ld-trust-grid { grid-template-columns: 1fr 1fr !important; }
          .ld-steps-grid { grid-template-columns: 1fr !important; }
          .ld-stats-grid { grid-template-columns: 1fr !important; }
          .ld-footer-grid { grid-template-columns: 1fr 1fr !important; gap: 32px 20px !important; }
          .ld-section-pad { padding: 56px 24px !important; }
          .ld-hero-h1 { font-size: 34px !important; }
          .ld-hero-sub { font-size: 15px !important; }
          .ld-hero-btns { flex-direction: column !important; }
          .ld-mockup { display: none !important; }
          .ld-compare-scroll { overflow-x: auto !important; }
        }
        @media (max-width: 480px) {
          .ld-trust-grid { grid-template-columns: 1fr !important; }
          .ld-stats-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ── NAVBAR ──────────────────────────────────────────────────────────── */}
      <nav className="ld-nav" style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: scrolled
          ? 'rgba(15,23,42,0.88)'
          : 'rgba(15,23,42,0.6)',
        backdropFilter: 'blur(24px) saturate(180%)',
        WebkitBackdropFilter: 'blur(24px) saturate(180%)',
        borderBottom: `1px solid ${scrolled ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.06)'}`,
        boxShadow: scrolled ? '0 2px 20px rgba(0,0,0,0.25)' : 'none',
        transition: 'all 0.2s ease',
        padding: '0 48px', height: 64,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Logo/>

        {/* Center nav links */}
        <div className="ld-nav-links" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {navLinks.map(l => (
            <button key={l.label} onClick={l.action} className="ld-nav-link"
              style={{
                fontSize: 14, fontWeight: 500, color: 'rgba(255,255,255,0.7)', padding: '8px 14px',
                background: 'none', border: 'none', cursor: 'pointer',
                fontFamily: FB, borderRadius: 6,
              }}>
              {l.label}
            </button>
          ))}
        </div>

        {/* Right CTAs */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={onLogin} className="ld-btn-outline-white" style={{ fontSize: 13, padding: '8px 18px' }}>
            Sign In
          </button>
          <button onClick={onSignUp} className="ld-btn-primary" style={{ fontSize: 13, padding: '8px 18px' }}>
            Start Free Trial
          </button>
          {/* Hamburger */}
          <button className="ld-hamburger"
            onClick={() => setMenuOpen(m => !m)}
            style={{
              display: 'none', background: 'none', border: 'none',
              cursor: 'pointer', padding: 4, flexDirection: 'column', gap: 4,
            }}>
            {[0,1,2].map(i => (
              <div key={i} style={{ width: 22, height: 2, background: T2, borderRadius: 2, transition: 'all 0.2s',
                transform: menuOpen ? (i === 0 ? 'rotate(45deg) translateY(6px)' : i === 2 ? 'rotate(-45deg) translateY(-6px)' : 'scaleX(0)') : 'none',
              }}/>
            ))}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {menuOpen && (
        <div style={{
          position: 'fixed', top: 64, left: 0, right: 0, bottom: 0,
          background: W, zIndex: 99, padding: '24px 32px',
          display: 'flex', flexDirection: 'column', gap: 4,
          borderTop: `1px solid ${BD}`,
        }}>
          {navLinks.map(l => (
            <button key={l.label} onClick={l.action}
              style={{
                fontSize: 18, fontWeight: 600, color: T1, padding: '14px 0',
                background: 'none', border: 'none', cursor: 'pointer',
                fontFamily: FH, textAlign: 'left', borderBottom: `1px solid ${BD}`,
              }}>
              {l.label}
            </button>
          ))}
          <button onClick={onLogin} style={{ fontSize: 15, color: T2, padding: '14px 0', background: 'none', border: 'none', cursor: 'pointer', fontFamily: FB, textAlign: 'left' }}>
            Sign In
          </button>
          <button onClick={onSignUp} className="ld-btn-primary" style={{ fontSize: 15, padding: '14px', marginTop: 8 }}>
            Start Free Trial →
          </button>
        </div>
      )}

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section className="ld-hero-section" style={{
        background: `
          radial-gradient(ellipse at 20% 50%, rgba(59,130,246,0.25) 0%, transparent 60%),
          radial-gradient(ellipse at 80% 20%, rgba(99,102,241,0.2) 0%, transparent 60%),
          radial-gradient(ellipse at 60% 80%, rgba(14,165,233,0.15) 0%, transparent 50%),
          ${N}
        `,
        padding: '96px 48px 80px', overflow: 'hidden', position: 'relative',
      }}>
        {/* Floating orb */}
        <div className="hero-orb"/>
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div className="ld-hero-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }}>
            {/* Left */}
            <div>
              <div className="glass-pill" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 28,
                background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.35)',
              }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e',
                  animation: 'ldpulse 2s ease-in-out infinite' }}/>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#93c5fd', fontFamily: 'var(--mono)', letterSpacing: '0.06em' }}>
                  CMMC PHASE 2 — NOVEMBER 2026
                </span>
              </div>

              <h1 className="ld-hero-h1" style={{
                fontSize: 48, fontWeight: 800, color: W, lineHeight: 1.12,
                marginBottom: 22, letterSpacing: '-0.03em', fontFamily: FH,
              }}>
                CMMC Level 2 compliance without the consultant fees.
              </h1>

              <p className="ld-hero-sub" style={{
                fontSize: 17, color: '#94a3b8', lineHeight: 1.72,
                maxWidth: 520, marginBottom: 36,
              }}>
                TheClearAudit automates your path to DoD contract eligibility — gap analysis,
                evidence collection, policy templates, and audit-ready exports in one platform.
              </p>

              <div className="ld-hero-btns" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
                <button onClick={onSignUp} className="ld-btn-primary" style={{ fontSize: 15, padding: '13px 28px' }}>
                  Start Free 7-Day Trial
                </button>
                <button onClick={() => scrollTo('how-it-works')} className="ld-btn-outline-white" style={{ fontSize: 15, padding: '13px 24px' }}>
                  See How It Works ↓
                </button>
              </div>

              <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
                Trusted by defense contractors across the US ·{' '}
                <span style={{ color: '#94a3b8' }}>CMMC · NIST 800-171 · SOC 2 · ISO 27001</span>
              </div>
            </div>

            {/* Right: mockup */}
            <div className="ld-mockup" style={{
              display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
              animation: 'heroFloat 5s ease-in-out infinite',
            }}>
              <DashboardMockup/>
            </div>
          </div>
        </div>
      </section>

      {/* ── SOCIAL PROOF BAR (Frameworks) ────────────────────────────────────── */}
      <section id="frameworks" className="ld-frameworks-bar" style={{ background: G, borderTop: `1px solid ${BD}`, borderBottom: `1px solid ${BD}`, padding: '28px 48px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: T3, textTransform: 'uppercase', letterSpacing: '0.07em', flexShrink: 0 }}>
            Frameworks Supported:
          </span>
          {['CMMC 2.0', 'NIST SP 800-171', 'SOC 2', 'ISO 27001', 'HIPAA', 'NIST CSF 2.0', 'PCI DSS v4.0', 'FedRAMP Moderate'].map(fw => (
            <span key={fw} style={{
              fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 20,
              background: W, border: `1px solid ${BD}`, color: T2,
              fontFamily: 'var(--mono)',
            }}>
              {fw}
            </span>
          ))}
        </div>
      </section>

      {/* ── PROBLEM STATS ─────────────────────────────────────────────────────── */}
      <section className="ld-section-pad" style={{ ...sec(W), padding: '80px 48px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <FadeIn>
            <div className="ld-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
              {[
                { value: '220,000+', label: 'Defense contractors required to achieve CMMC certification by November 2026', color: B },
                { value: '< 1%',    label: 'Of contractors are currently CMMC certified — the window is closing fast', color: '#ef4444' },
                { value: '$45,000+', label: 'Average cost of hiring a CMMC consultant — TheClearAudit costs a fraction of that', color: AM },
              ].map(s => (
                <div key={s.value} style={{
                  background: W, border: `1px solid ${BD}`, borderRadius: 12,
                  padding: '32px 28px', textAlign: 'center',
                  boxShadow: '0 2px 12px rgba(15,23,42,0.05)',
                }}>
                  <div style={{ fontSize: 44, fontWeight: 800, color: s.color, marginBottom: 12, fontFamily: FH, letterSpacing: '-0.02em', lineHeight: 1 }}>
                    {s.value}
                  </div>
                  <div style={{ fontSize: 14, color: T2, lineHeight: 1.6 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── HOW IT WORKS ──────────────────────────────────────────────────────── */}
      <section id="how-it-works" className="ld-section-pad" style={{
        background: `radial-gradient(ellipse at 30% 60%, rgba(59,130,246,0.12) 0%, transparent 60%), ${N}`,
        borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)',
        padding: '88px 48px',
      }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 56 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: B, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12, fontFamily: 'var(--mono)' }}>
                HOW IT WORKS
              </div>
              <h2 style={{ fontSize: 36, fontWeight: 800, color: W, marginBottom: 14, letterSpacing: '-0.02em', fontFamily: FH }}>
                From gap to audit-ready in three steps
              </h2>
              <p style={{ fontSize: 15, color: '#94a3b8', maxWidth: 540, margin: '0 auto', lineHeight: 1.7 }}>
                TheClearAudit guides you through the entire CMMC preparation process — no prior compliance experience required.
              </p>
            </div>
          </FadeIn>

          <div className="ld-steps-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {[
              {
                n: '01', icon: 'search',
                title: 'Connect & Assess',
                desc: 'Answer our guided questionnaire to instantly see your compliance score across all 110 NIST 800-171 controls. No security expertise required — every question is in plain English.',
              },
              {
                n: '02', icon: 'list',
                title: 'Fix & Document',
                desc: 'Follow your personalized remediation plan. Generate your SSP, POA&M, and all 7 policy templates in one click. Track progress across every control domain.',
              },
              {
                n: '03', icon: 'shield',
                title: 'Pass Your Audit',
                desc: 'Export your complete evidence package, share read-only auditor access, and walk into your C3PAO assessment with all documentation ready. No last-minute scrambling.',
              },
            ].map((s, i) => (
              <FadeIn key={s.n} delay={i * 0.12}>
                <div className="glass-card glass-shine" style={{
                  borderRadius: 14, padding: '32px 28px', position: 'relative',
                }}>
                  <div style={{
                    position: 'absolute', top: 24, right: 24,
                    fontSize: 32, fontWeight: 800, color: 'rgba(255,255,255,0.06)',
                    fontFamily: FH, lineHeight: 1,
                  }}>{s.n}</div>
                  <div style={{
                    width: 48, height: 48, borderRadius: 12,
                    background: 'rgba(59,130,246,0.15)', border: '1.5px solid rgba(59,130,246,0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: 20,
                  }}>
                    <Ico k={s.icon} size={22} color={B}/>
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: W, marginBottom: 10, fontFamily: FH }}>{s.title}</div>
                  <div style={{ fontSize: 13.5, color: '#94a3b8', lineHeight: 1.7 }}>{s.desc}</div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ──────────────────────────────────────────────────────────── */}
      <section id="features" className="ld-section-pad" style={{ ...sec(W), padding: '88px 48px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 52 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: B, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12, fontFamily: 'var(--mono)' }}>
                FEATURES
              </div>
              <h2 style={{ fontSize: 36, fontWeight: 800, color: N, marginBottom: 14, letterSpacing: '-0.02em', fontFamily: FH }}>
                Everything you need to get compliant
              </h2>
              <p style={{ fontSize: 15, color: T2, maxWidth: 520, margin: '0 auto', lineHeight: 1.7 }}>
                Purpose-built for defense contractors — not a generic GRC tool with compliance features bolted on.
              </p>
            </div>
          </FadeIn>

          <div className="ld-features-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
            {features.map((f, i) => (
              <FadeIn key={f.title} delay={Math.floor(i / 3) * 0.08 + (i % 3) * 0.06}>
                <div className="ld-feature-card" style={{
                  background: W, border: `1px solid ${BD}`, borderRadius: 12,
                  padding: '26px 24px',
                  boxShadow: '0 1px 4px rgba(15,23,42,0.04)',
                  height: '100%',
                }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 10,
                    background: '#eff6ff', border: `1px solid #bfdbfe`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: 16,
                  }}>
                    <Ico k={f.icon} size={20} color={B}/>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: N, marginBottom: 8, fontFamily: FH }}>{f.title}</div>
                  <div style={{ fontSize: 13.5, color: T2, lineHeight: 1.7 }}>{f.desc}</div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── EARLY ACCESS CTA ──────────────────────────────────────────────────── */}
      <section className="ld-section-pad" style={{ ...sec(G, BD), padding: '88px 48px' }}>
        <div style={{ maxWidth: 680, margin: '0 auto', textAlign: 'center' }}>
          <FadeIn>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'rgba(45,125,210,0.12)', border: '1px solid rgba(45,125,210,0.3)',
              borderRadius: 20, padding: '6px 16px', marginBottom: 24,
              fontSize: 11, fontWeight: 700, color: B, textTransform: 'uppercase', letterSpacing: '0.1em',
              fontFamily: 'var(--mono)',
            }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: B, display: 'inline-block' }} />
              Now in early access
            </div>
            <h2 style={{ fontSize: 36, fontWeight: 800, color: N, marginBottom: 16, letterSpacing: '-0.02em', fontFamily: FH, lineHeight: 1.2 }}>
              Be one of our first customers
            </h2>
            <p style={{ fontSize: 15, color: T2, lineHeight: 1.7, marginBottom: 32, maxWidth: 520, margin: '0 auto 32px' }}>
              TheClearAudit is built specifically for defense contractors facing the November 2026 CMMC deadline.
              Early customers get direct access to our team and help shape the product.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 40 }}>
              <button onClick={onSignUp} className="ld-btn-primary" style={{ fontSize: 14, padding: '13px 32px' }}>
                Start Free Trial
              </button>
              <a
                href="mailto:theclearaudit@gmail.com"
                style={{
                  fontSize: 14, padding: '13px 28px', borderRadius: 8, fontWeight: 600,
                  border: `1px solid ${BD}`, color: T2, textDecoration: 'none',
                  display: 'inline-block', transition: 'all 0.15s',
                  fontFamily: 'var(--font)',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = B; e.currentTarget.style.color = N; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = BD; e.currentTarget.style.color = T2; }}
              >
                Contact Us
              </a>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 28, flexWrap: 'wrap' }}>
              {[
                '7-day free trial — no credit card required',
                'Cancel anytime',
                'Direct line to our team',
              ].map(item => (
                <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: T3 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={B} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  {item}
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── PRICING ───────────────────────────────────────────────────────────── */}
      <section id="pricing" className="ld-section-pad" style={{
        background: `radial-gradient(ellipse at 70% 40%, rgba(99,102,241,0.12) 0%, transparent 60%), ${N}`,
        borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)',
        padding: '88px 48px',
      }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 52 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: B, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12, fontFamily: 'var(--mono)' }}>
                PRICING
              </div>
              <h2 style={{ fontSize: 36, fontWeight: 800, color: W, marginBottom: 14, letterSpacing: '-0.02em', fontFamily: FH }}>
                Get Certified Before the November 2026 Deadline
              </h2>
              <p style={{ fontSize: 15, color: '#94a3b8', maxWidth: 520, margin: '0 auto', lineHeight: 1.7 }}>
                CMMC Phase 2 enforcement begins November 2026. Contractors without certification risk losing DoD contracts. All plans include a 7-day free trial — no credit card required.
              </p>
            </div>
          </FadeIn>

          <div className="ld-pricing-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20, maxWidth: 800, margin: '0 auto' }}>
            {plans.map((plan, i) => (
              <FadeIn key={plan.name} delay={i * 0.07}>
                <div className={`ld-plan-card ${plan.popular ? 'glass-blue glass-shine' : 'glass-card glass-shine'}`} style={{
                  borderRadius: 14, padding: 24,
                  position: 'relative', display: 'flex', flexDirection: 'column',
                  ...(plan.popular ? {
                    border: `2px solid rgba(59,130,246,0.5)`,
                    boxShadow: '0 12px 48px rgba(59,130,246,0.25), inset 0 1px 0 rgba(255,255,255,0.15)',
                  } : {}),
                }}>
                  {plan.popular && (
                    <div style={{
                      position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                      background: B, color: W, fontSize: 10, fontWeight: 700,
                      padding: '3px 14px', borderRadius: 20, whiteSpace: 'nowrap',
                      fontFamily: FH,
                    }}>Best Value</div>
                  )}
                  <div style={{ fontSize: 15, fontWeight: 700, color: W, marginBottom: 6, fontFamily: FH }}>{plan.name}</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 2, marginBottom: 4 }}>
                    <span style={{ fontSize: 30, fontWeight: 800, color: W, fontFamily: FH, lineHeight: 1 }}>{plan.price}</span>
                    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{plan.period}</span>
                  </div>
                  <div style={{ fontSize: 11, color: plan.popular ? '#fbbf24' : '#d1a754', marginBottom: 12, fontWeight: 500 }}>{plan.setup}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 18, lineHeight: 1.55 }}>{plan.desc}</div>
                  <ul style={{ flex: 1, listStyle: 'none', padding: 0, margin: '0 0 20px 0', display: 'flex', flexDirection: 'column', gap: 9 }}>
                    {plan.features.map(f => (
                      <li key={f} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, color: '#e2e8f0', lineHeight: 1.4 }}>
                        <Check color={plan.popular ? '#4ade80' : GR}/>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button onClick={onSignUp}
                    className={plan.popular ? 'ld-btn-white' : 'ld-btn-primary'}
                    style={{ fontSize: 13, padding: '10px', width: '100%' }}>
                    Get Started Free
                  </button>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn>
            <div style={{ textAlign: 'center', marginTop: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={GR} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <span style={{ fontSize: 13, color: '#475569', fontStyle: 'italic' }}>
                vs. $45,000+ for a CMMC consultant or $15,000+/year for enterprise compliance tools
              </span>
            </div>
          </FadeIn>

          <FadeIn>
            <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: '#475569' }}>
              All prices in USD. One-time setup fee charged at first subscription.{' '}
              <span style={{ color: '#93c5fd', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => scrollTo('pricing')}>
                Compare plans
              </span>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── SECURITY & TRUST ──────────────────────────────────────────────────── */}
      <section id="trust" className="ld-section-pad" style={{
        background: `radial-gradient(ellipse at 50% 80%, rgba(59,130,246,0.1) 0%, transparent 60%), ${N}`,
        borderTop: '1px solid rgba(255,255,255,0.05)',
        padding: '88px 48px',
      }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 52 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: B, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12, fontFamily: 'var(--mono)' }}>
                SECURITY
              </div>
              <h2 style={{ fontSize: 36, fontWeight: 800, color: W, marginBottom: 14, letterSpacing: '-0.02em', fontFamily: FH }}>
                Built for organizations that handle sensitive data
              </h2>
              <p style={{ fontSize: 15, color: '#94a3b8', maxWidth: 520, margin: '0 auto', lineHeight: 1.7 }}>
                We take data security as seriously as you take compliance.
              </p>
            </div>
          </FadeIn>

          <div className="ld-trust-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
            {trustCards.map((c, i) => (
              <FadeIn key={c.title} delay={i * 0.07}>
                <div className="glass-card glass-shine ld-feature-card" style={{
                  borderRadius: 12, padding: '24px 20px', textAlign: 'center',
                }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: 12,
                    background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 14px',
                  }}>
                    <Ico k={c.icon} size={22} color={B}/>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: W, marginBottom: 8, fontFamily: FH }}>{c.title}</div>
                  <div style={{ fontSize: 12.5, color: '#94a3b8', lineHeight: 1.65 }}>{c.desc}</div>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn>
            <div className="glass-card" style={{
              borderRadius: 10, padding: '14px 20px', textAlign: 'center', fontSize: 13,
              background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)',
              color: '#fde68a',
            }}>
              <strong>AI Safety Notice:</strong> AI features are designed to answer compliance framework questions only.
              Never enter classified information, CUI, contract numbers, or sensitive technical specifications into the AI chat.
              {onSecurity && (
                <span style={{ color: '#93c5fd', cursor: 'pointer', marginLeft: 6, textDecoration: 'underline' }}
                  onClick={onSecurity}>
                  View full security documentation →
                </span>
              )}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────────────── */}
      <section className="ld-section-pad" style={{ ...sec(G, BD), padding: '88px 48px' }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 52 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: B, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12, fontFamily: 'var(--mono)' }}>
                FAQ
              </div>
              <h2 style={{ fontSize: 36, fontWeight: 800, color: N, marginBottom: 14, letterSpacing: '-0.02em', fontFamily: FH }}>
                Frequently asked questions
              </h2>
              <p style={{ fontSize: 15, color: T2, maxWidth: 480, margin: '0 auto', lineHeight: 1.7 }}>
                Everything you need to know about TheClearAudit and CMMC preparation.
              </p>
            </div>
          </FadeIn>

          <div style={{ background: W, border: `1px solid ${BD}`, borderRadius: 14, overflow: 'hidden' }}>
            {faqs.map((faq, i) => (
              <div key={i} style={{ borderBottom: i < faqs.length - 1 ? `1px solid ${BD}` : 'none' }}>
                <button className="ld-faq-q"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{
                    width: '100%', padding: '20px 24px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    gap: 16, textAlign: 'left',
                    color: openFaq === i ? B : N,
                  }}>
                  <span style={{ fontSize: 14, fontWeight: 600, fontFamily: FH, lineHeight: 1.4 }}>{faq.q}</span>
                  <span style={{
                    fontSize: 20, color: openFaq === i ? B : T3, flexShrink: 0,
                    transform: openFaq === i ? 'rotate(45deg)' : 'none',
                    transition: 'transform 0.2s, color 0.2s',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 24, height: 24,
                  }}>+</span>
                </button>
                {openFaq === i && (
                  <div style={{
                    padding: '0 24px 20px', fontSize: 13.5, color: T2,
                    lineHeight: 1.75, animation: 'fadeIn 0.2s ease both',
                  }}>
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ─────────────────────────────────────────────────────────── */}
      <section className="ld-section-pad" style={{
        background: `linear-gradient(135deg, ${N} 0%, #1e3a5f 100%)`,
        borderTop: '1px solid rgba(59,130,246,0.2)',
        padding: '88px 48px', textAlign: 'center',
      }}>
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)',
            borderRadius: 20, padding: '5px 14px', marginBottom: 24,
          }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444',
              animation: 'ldpulse 1.5s ease-in-out infinite' }}/>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#93c5fd', fontFamily: 'var(--mono)', letterSpacing: '0.06em' }}>
              DEADLINE APPROACHING
            </span>
          </div>
          <h2 style={{
            fontSize: 38, fontWeight: 800, color: W, marginBottom: 18,
            letterSpacing: '-0.02em', fontFamily: FH, lineHeight: 1.2,
          }}>
            CMMC Phase 2 enforcement begins November 2026.
          </h2>
          <p style={{ fontSize: 16, color: '#94a3b8', marginBottom: 36, lineHeight: 1.7 }}>
            Get audit-ready before the deadline. Most contractors need 6–18 months to prepare.
            Start your gap analysis today — free for 7 days, no credit card required.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={onSignUp} className="ld-btn-primary" style={{ fontSize: 15, padding: '14px 32px' }}>
              Start Free Trial
            </button>
            <button onClick={onLogin} className="ld-btn-outline-white" style={{ fontSize: 15, padding: '14px 28px' }}>
              Sign In to Dashboard
            </button>
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 16 }}>
            No credit card required · Cancel anytime · Setup in under 10 minutes
          </div>
        </div>
      </section>

      {/* ── FOOTER ────────────────────────────────────────────────────────────── */}
      <footer className="ld-section-pad" style={{ background: N, borderTop: '1px solid rgba(255,255,255,0.06)', padding: '64px 48px 36px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div className="ld-footer-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: '40px 32px', marginBottom: 48 }}>
            {/* Brand */}
            <div>
              <Logo light/>
              <div style={{ fontSize: 12, color: '#4a5568', lineHeight: 1.7, maxWidth: 280, marginTop: 14, marginBottom: 20 }}>
                TheClearAudit helps defense contractors and regulated organizations achieve compliance across CMMC, SOC 2, ISO 27001, HIPAA, FedRAMP, PCI DSS, and NIST CSF 2.0.
              </div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 10, fontWeight: 600, color: '#93c5fd',
                fontFamily: 'var(--mono)', letterSpacing: '0.05em',
                padding: '4px 10px', borderRadius: 4,
                background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.18)',
              }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#4ade80', display: 'inline-block' }}/>
                CMMC Phase 2 — Nov 2026
              </div>
            </div>

            {/* Product */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 18 }}>Product</div>
              {[
                { label: 'Features',    action: () => scrollTo('features') },
                { label: 'Pricing',     action: () => scrollTo('pricing') },
                { label: 'Frameworks',  action: () => scrollTo('frameworks') },
                { label: 'How It Works', action: () => scrollTo('how-it-works') },
                { label: 'Security',    action: onSecurity || (() => scrollTo('trust')) },
              ].map(l => (
                <div key={l.label} style={{ marginBottom: 11 }}>
                  <span onClick={l.action} style={{ fontSize: 13, color: '#4a5568', cursor: 'pointer', transition: 'color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#e2e8f0'}
                    onMouseLeave={e => e.currentTarget.style.color = '#4a5568'}>
                    {l.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Frameworks */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 18 }}>Frameworks</div>
              {['CMMC 2.0', 'NIST SP 800-171', 'SOC 2 Type II', 'ISO 27001', 'HIPAA', 'NIST CSF 2.0', 'PCI DSS v4.0', 'FedRAMP Moderate'].map(fw => (
                <div key={fw} style={{ marginBottom: 9 }}>
                  <span style={{ fontSize: 12, color: '#4a5568' }}>{fw}</span>
                </div>
              ))}
            </div>

            {/* Company */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 18 }}>Company</div>
              {[
                { label: 'About',    action: () => scrollTo('trust') },
                { label: 'Contact',  action: () => window.location.href = 'mailto:theclearaudit@gmail.com' },
                { label: 'Security', action: onSecurity || (() => scrollTo('trust')) },
              ].map(l => (
                <div key={l.label} style={{ marginBottom: 11, display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span onClick={l.action || undefined} style={{ fontSize: 13, color: '#4a5568', cursor: l.action ? 'pointer' : 'default', transition: 'color 0.15s' }}
                    onMouseEnter={e => l.action && (e.currentTarget.style.color = '#e2e8f0')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#4a5568')}>
                    {l.label}
                  </span>
                  {l.soon && (
                    <span style={{ fontSize: 9, fontWeight: 700, color: '#4a5568', background: '#1e293b', padding: '1px 5px', borderRadius: 3, border: '1px solid #334155' }}>SOON</span>
                  )}
                </div>
              ))}
            </div>

            {/* Legal */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 18 }}>Legal</div>
              {[
                { label: 'Terms of Service',   action: onTerms },
                { label: 'Privacy Policy',     action: onPrivacy },
                { label: 'Data Processing Agreement', action: onDPA },
                { label: 'Acceptable Use Policy',     action: onAUP },
                { label: 'CUI Handling Policy',       action: onCUI },
                { label: 'SLA',                       action: onSLA },
              ].map(l => (
                <div key={l.label} style={{ marginBottom: 11 }}>
                  <span onClick={l.action || undefined} style={{ fontSize: 13, color: '#4a5568', cursor: l.action ? 'pointer' : 'default', transition: 'color 0.15s' }}
                    onMouseEnter={e => l.action && (e.currentTarget.style.color = '#e2e8f0')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#4a5568')}>
                    {l.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom bar */}
          <div style={{
            borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 24,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            flexWrap: 'wrap', gap: 12,
          }}>
            <div style={{ fontSize: 12, color: '#4a5568' }}>
              © 2026 TheClearAudit. All rights reserved.
            </div>
            <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: '#4a5568' }}>theclearaudit@gmail.com</span>
              <span style={{ fontSize: 12, color: '#4a5568' }}>theclearaudit.com</span>
              {[
                { label: 'Terms', action: onTerms },
                { label: 'Privacy', action: onPrivacy },
                { label: 'DPA', action: onDPA },
                { label: 'AUP', action: onAUP },
                { label: 'CUI Policy', action: onCUI },
                { label: 'SLA', action: onSLA },
              ].map(l => (
                <span key={l.label} onClick={l.action || undefined}
                  style={{ fontSize: 12, color: '#4a5568', cursor: l.action ? 'pointer' : 'default' }}
                  onMouseEnter={e => l.action && (e.currentTarget.style.color = '#e2e8f0')}
                  onMouseLeave={e => (e.currentTarget.style.color = '#4a5568')}>
                  {l.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
}
